/*
 *  Copyright (c) 2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { ChartType } from "~/generated/graphql";
import {
  DateRange,
  EstatePeriod,
  Granularity,
  ChartType as CostAnalysisChartType,
} from "~/components";
import {
  EstateGroupBy,
  EstateRecordsGroupingChart,
  EstateRecordsGroupingTable,
  TotalCostTable,
  useEstateRecordsCost,
  useEstateRecordsGroupByChartJob,
} from "~/views/estate/components/Charts";
import { SERVICE_SORT_OPTIONS } from "./components/ParametersBar/types";
import { isTableDataSortable, sortDataByTotal } from "./helpers";

type Props = {
  period: EstatePeriod;
  granularity: Granularity;
  range: DateRange;
};

export function EstateRecordsServiceView({
  period,
  granularity,
  range,
}: Props) {
  const {
    startDate,
    endDate,
    isChartDataLoading,
    isTableDataLoading,
    chartData,
    tableData,
    tableError,
    sortField,
    sortDirection,
    onSortSelect,
    onSortOrderChange,
    jobId,
  } = useEstateRecordsGroupByChartJob({
    chartType: ChartType.Service,
    range,
  });

  const {
    costPerGroupChartData,
    costPerGroupTableData,
    tableCount,
    extraDetails,
    allColumns,
    stackBarColors,
    chartKeys,
    firstColumnWidth,
  } = useEstateRecordsCost({
    chartData,
    tableData,
    granularity,
    period,
    startDate,
    endDate,
    chartType: CostAnalysisChartType.COST_PER_SERVICES,
  });

  const sortedCostPerCategoryTableData = isTableDataSortable(sortField, period)
    ? sortDataByTotal(costPerGroupTableData, sortDirection)
    : costPerGroupTableData;

  return (
    <>
      <EstateRecordsGroupingChart
        data={costPerGroupChartData}
        granularity={granularity}
        loading={isChartDataLoading}
        startDate={startDate}
        endDate={endDate}
        period={period}
        colors={stackBarColors}
        keys={chartKeys}
        chartType={CostAnalysisChartType.COST_PER_SERVICES}
      />
      <TotalCostTable
        period={period}
        range={range}
        count={tableData?.chartJobTable?.data?.count ?? 0}
        isLoading={isTableDataLoading}
        chartType={EstateGroupBy.SERVICE}
        totalTop={extraDetails?.totalTop ?? "-"}
        totalOther={extraDetails?.totalOther ?? "-"}
      />
      <EstateRecordsGroupingTable
        loading={isTableDataLoading}
        sortField={sortField}
        sortDirection={sortDirection}
        sortOptions={SERVICE_SORT_OPTIONS}
        tableCount={tableCount ?? 0}
        columns={allColumns}
        data={sortedCostPerCategoryTableData}
        firstColumnWidth={firstColumnWidth}
        error={tableError}
        period={period}
        jobId={jobId}
        onSortSelect={onSortSelect}
        onSortDirectionSelect={onSortOrderChange}
      />
    </>
  );
}
